import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardUtils } from '@/features/dashboard/utils/dashboardUtils';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useDashboardTotalApartmentsApi } from '@/features/dashboard/hooks/useDashboardTotalApartmentsApi';
import DashboardTotalApartmentsBlockSkeleton from '@/features/dashboard/components/DashboardTotalApartments/DashboardTotalApartmentsBlockSkeleton';

type Props = {
	isMobile?: boolean;
};

const DashboardTotalApartmentsBlock: FC<Props> = ({ isMobile }) => {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const { totalApartmentsData, isLoadingTotalApartmentsData } =
		useDashboardTotalApartmentsApi();

	const {
		icon: Icon,
		iconClasses,
		iconBgClasses,
		chartMainColor,
	} = DashboardUtils.getAllChartsConfig('totalApartments');

	return (
		<>
			{!!totalApartmentsData?.totalApartments && (
				<div
					className={cn(
						'p-6 flex grow  flex-col gap-6 border border-popover-100 rounded-2.5xl bg-popover-10 select-none',
						isMobile && 'p-0 border-0 bg-popover-30 gap-4',
					)}>
					{!isMobile && (
						<div className='flex items-center gap-4'>
							<div
								className={cn(
									'size-8 flex items-center justify-center rounded-semi-xl',
									iconBgClasses,
								)}>
								<Icon className={iconClasses} />
							</div>
							<h4 className='text-xl font-medium'>
								{t(
									'main.dashboard.components.chart.totalApartments.title',
								)}
							</h4>
						</div>
					)}
					<div>
						<h3
							className={cn(
								'text-2xl font-medium text-popover-1000',
								isMobile && 'text-xl',
							)}>
							{totalApartmentsData?.totalApartments}
						</h3>
					</div>
					<div className='grow-1 flex flex-col items-center gap-6'>
						<div className='w-full h-4 flex gap-0.5 rounded-full overflow-hidden'>
							{totalApartmentsData?.apartmentsByType?.map(
								(apartment, index) => (
									<div
										key={index}
										className={cn('h-full', chartMainColor)}
										style={{
											filter: `opacity(${
												1 - index * 0.15
											})`,
											width: `${
												(apartment.count /
													totalApartmentsData.totalApartments) *
												100
											}%`,
										}}
									/>
								),
							)}
						</div>
						<div className='w-full h-60 flex flex-col gap-6 overflow-y-scroll'>
							{totalApartmentsData?.apartmentsByType?.map(
								(type, index) => (
									<div
										key={index}
										className='flex items-center gap-2.5'>
										<div
											className={cn(
												'size-2 m-1.5 rounded-full',
												chartMainColor,
											)}
											style={{
												filter: `opacity(${
													1 - index * 0.15
												})`,
											}}
										/>
										<span className='text-sm text-popover-1000'>
											{type.apartmentType}
										</span>
										<span className='text-sm text-popover-1000 ml-auto'>
											{type.count}
										</span>
									</div>
								),
							)}
						</div>
					</div>
					<Button
						variant={'outline'}
						className='w-full text-sm font-medium'
						onClick={() => navigate('/apartments')}>
						{t(
							'main.dashboard.components.chart.totalApartments.seeMoreButton',
						)}
					</Button>
				</div>
			)}
			{!!isLoadingTotalApartmentsData && (
				<DashboardTotalApartmentsBlockSkeleton />
			)}
		</>
	);
};

export default DashboardTotalApartmentsBlock;
